import { RelativityMetadata } from '../../../../backend/src/integrations/relativity/relativity-metadata.entity';
import formatDate from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Label } from '@/new/components/shadcn/label';

interface RelativityMetadataDisplayProps {
  metadata: RelativityMetadata;
  compact?: boolean; // Use single column layout for popovers
}

// Helper function to convert camelCase to readable labels
const formatFieldLabel = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// Helper function to format file size
const formatFileSize = (bytes: number | null): string | null => {
  if (bytes === null) return null;
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const RelativityMetadataDisplay = ({ metadata, compact = false }: RelativityMetadataDisplayProps) => {
  // Standard fields with their values
  const standardFields: Array<{ label: string; value: any; type: 'text' | 'date' | 'array' | 'number' }> = [
    { label: 'Control Number', value: metadata.controlNumber, type: 'text' },
    { label: 'File Type', value: metadata.fileType, type: 'text' },
    { label: 'File Size', value: formatFileSize(metadata.fileSize), type: 'text' },
    { label: 'Email From', value: metadata.emailFrom, type: 'text' },
    { label: 'Email To', value: metadata.emailTo, type: 'array' },
    { label: 'Email Subject', value: metadata.emailSubject, type: 'text' },
    { label: 'Email Sent Date', value: metadata.emailSentDate, type: 'date' },
    { label: 'Email Received Date', value: metadata.emailReceivedDate, type: 'date' },
    { label: 'Document Date', value: metadata.documentDate, type: 'date' },
    { label: 'Author', value: metadata.author, type: 'text' },
    { label: 'Title', value: metadata.title, type: 'text' },
    { label: 'Container Name', value: metadata.containerName, type: 'text' },
    { label: 'Container ID', value: metadata.containerId, type: 'text' },
    { label: 'File Path', value: metadata.filePath, type: 'text' },
    { label: 'Page Count', value: metadata.pageCount, type: 'number' },
    { label: 'Issues', value: metadata.issues, type: 'array' },
  ];

  // Custom fields from JSONB
  const customFields: Array<{ label: string; value: any }> = [];
  if (metadata.customFields) {
    Object.entries(metadata.customFields).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        customFields.push({ label: formatFieldLabel(key), value });
      }
    });
  }

  // Filter out null/undefined standard fields and combine with custom fields
  const allFields = [
    ...standardFields.filter((field) => field.value !== null && field.value !== undefined),
    ...customFields.map((field) => ({ ...field, type: 'text' as const })),
  ];

  // If no fields to display, don't render anything
  if (allFields.length === 0) {
    return null;
  }

  const renderFieldValue = (field: { label: string; value: any; type: 'text' | 'date' | 'array' | 'number' }) => {
    switch (field.type) {
      case 'date':
        return <div className="text-sm text-gray-900">{formatDate(field.value)}</div>;
      case 'array':
        if (Array.isArray(field.value) && field.value.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {field.value.map((item, index) => (
                <Badge key={index} className="bg-purple-100/60 text-xs text-purple-700">
                  {item}
                </Badge>
              ))}
            </div>
          );
        }
        return <div className="text-sm text-gray-500">None</div>;
      case 'number':
        return <div className="text-sm text-gray-900">{field.value.toLocaleString()}</div>;
      case 'text':
      default:
        // Handle long text values
        const textValue = String(field.value);
        if (textValue.length > 100) {
          return (
            <div className="text-sm break-words text-gray-900" title={textValue}>
              {textValue.substring(0, 100)}...
            </div>
          );
        }
        return <div className="text-sm break-words text-gray-900">{textValue}</div>;
    }
  };

  return (
    <div className={compact ? 'w-[400px] space-y-4' : 'space-y-4'}>
      <Label className="flex items-center gap-2 text-xs text-purple-600 uppercase">
        <Icon className="lucide--database h-4 w-4" />
        Relativity Metadata
      </Label>
      <div
        className={`rounded-md border border-purple-200 bg-purple-50/30 p-4 ${
          compact ? 'grid grid-cols-1 gap-y-3' : 'grid grid-cols-2 gap-x-6 gap-y-4'
        }`}
      >
        {allFields.map((field, index) => (
          <div key={index} className="min-w-0 space-y-1">
            <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">{field.label}</Label>
            {renderFieldValue(field)}
          </div>
        ))}
      </div>
    </div>
  );
};
